import { useState, useEffect, useCallback, useRef } from 'react';
import AMapLoader from '@amap/amap-jsapi-loader';
import { MAP_CONFIG } from '../utils/constants';
import { AMapInstance, AMapGeocoder } from '../types';

interface UseAMapReturn {
  AMap: any | null;
  map: AMapInstance | null;
  geocoder: AMapGeocoder | null;
  loading: boolean;
  error: string | null;
  initMap: (containerId?: string) => Promise<void>;
  cleanup: () => void;
}

/**
 * 高德地图Hook
 */
export const useAMap = (apiKey: string, securityKey: string): UseAMapReturn => {
  const [AMap, setAMap] = useState<any | null>(null);
  const [map, setMap] = useState<AMapInstance | null>(null);
  const [geocoder, setGeocoder] = useState<AMapGeocoder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<AMapInstance | null>(null);

  // 初始化地图
  const initMap = useCallback(async (containerId: string = 'map'): Promise<void> => {
    if (!apiKey || !securityKey) {
      setError('请先配置API Key和安全密钥');
      return;
    }

    // AMapLoader是通过import导入的，不需要检查window.AMapLoader
    if (!AMapLoader) {
      setError('AMapLoader模块导入失败');
      return;
    }

    // 检查容器是否存在
    const container = document.getElementById(containerId);
    if (!container) {
      setError(`地图容器 ${containerId} 不存在`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 设置安全密钥
      (window as any)._AMapSecurityConfig = {
        securityJsCode: securityKey
      };

      // 加载高德地图
      const AMapInstance = await AMapLoader.load({
        key: apiKey,
        version: '2.0',
        plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.Geocoder']
      });

      setAMap(AMapInstance);

      // 创建地图实例
      const mapInstance = new AMapInstance.Map(containerId, {
        viewMode: '3D',
        zoom: MAP_CONFIG.DEFAULT_ZOOM,
        center: MAP_CONFIG.DEFAULT_CENTER,
        mapStyle: MAP_CONFIG.MAP_STYLE,
        features: ['bg', 'road', 'building', 'point']
      }) as AMapInstance;

      // 添加控件
      mapInstance.add(new AMapInstance.Scale());
      mapInstance.add(new AMapInstance.ToolBar());

      // 创建地理编码器
      const geocoderInstance = new AMapInstance.Geocoder({
        radius: 1000,
        extensions: 'all'
      }) as AMapGeocoder;

      setMap(mapInstance);
      mapRef.current = mapInstance;
      setGeocoder(geocoderInstance);

      console.log('地图初始化成功');

      // 强制更新状态，确保组件重新渲染
      setTimeout(() => {
        console.log('强制状态更新');
      }, 100);
    } catch (err) {
      console.error('地图初始化失败:', err);
      setError('地图初始化失败，请检查API Key和安全密钥是否正确');
    } finally {
      setLoading(false);
    }
  }, [apiKey, securityKey]);

  // 清理函数
  const cleanup = useCallback((): void => {
    if (mapRef.current) {
      (mapRef.current as any).destroy();
      mapRef.current = null;
    }
    setMap(null);
    setAMap(null);
    setGeocoder(null);
  }, []);

  // API密钥变化时清理旧实例（仅在密钥变化时）
  useEffect(() => {
    return () => {
      // 仅在密钥变化或卸载时清理
      if (mapRef.current) {
        (mapRef.current as any).destroy();
        mapRef.current = null;
      }
    };
  }, [apiKey, securityKey]);

  return {
    AMap,
    map,
    geocoder,
    loading,
    error,
    initMap,
    cleanup
  };
};
